import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Icon } from '@simplybusiness/mobius';
import { menu, cross } from '@simplybusiness/icons';
import Logo from 'components/Logo';
import NavContent from 'components/NavContent';
import { getUser, isUserLoggedIn } from 'resources/user/selectors';
import { trackStructEvent } from 'services/TrackingService';
import CallBlock from './CallBlock';
import './Header.css';

const publicRoutes = [
  '/sign-in',
  '/sign-in/password',
  '/sign-in/token',
  '/reset-forgotten-password',
  '/forgot-password',
  '/create-password',
  '/confirm-email',
];

const callBlockRoutes = [
  '/sign-in',
  '/sign-in/password',
  '/forgot-password',
  '/create-password',
];

const Header = () => {
  const location = useLocation();
  const user = useSelector(getUser);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const shouldShowCallBlock = callBlockRoutes.includes(location.pathname);
  const isNavExcludedRoute = location.pathname === '/cancel-request';
  const shouldRenderMenu = !publicRoutes.includes(location.pathname) && !isNavExcludedRoute && isUserLoggedIn(user);

  const triggerEvent = (action: string) => {
    trackStructEvent({
      category: 'customer_account_menu_navigation',
      label: action === 'open' ? 'opened_menu' : 'closed_menu',
      action: action === 'open' ? 'opened_menu' : 'closed_menu',
      property: user?.customerAccountId || 'no_customer_account_retrieved',
    });
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    triggerEvent('close');
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
    triggerEvent('open');
  };

  const toggleDrawer = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  return (
    <>
      <div className="header-container" data-testid="header">
        <Link to="/" aria-label="Simply Business"><Logo /></Link>
        {shouldRenderMenu && (
          <nav aria-label="Primary navigation">
            <button
              type="button"
              className="hamburger-icon"
              onClick={toggleDrawer}
              aria-label={isDrawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isDrawerOpen}
              data-testid="hamburger-icon"
            >
              <Icon
                icon={isDrawerOpen ? cross : menu}
                size="md"
                className="hamburger-menu-icon"
                aria-hidden="true"
               
              />
            </button>
          </nav>
        )}
        {shouldShowCallBlock && <CallBlock page={location.pathname} />}
      </div>
      {shouldRenderMenu && isDrawerOpen && createPortal(
        <div
          className="mobile-nav-overlay"
          onClick={toggleDrawer}
          onKeyDown={e => e.key === 'Enter' && toggleDrawer}
          role="button"
          tabIndex={0}
        >
          <div
            className="mobile-nav-panel"
            onClick={e => e.stopPropagation()}
            role="presentation"
          >
            <NavContent onNavigate={closeDrawer} />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default Header;
